// 공정 체크 목록 (기공소 전용, 휴대폰 우선) — 기획서_QR공정체크_2026-08.md §5-2 / 인계서 작업 C
// 제작 현황(/production)의 '공정 체크' 보기에서 렌더된다. 저장은 부모(ProductionBoard)가 한다.
//
// 확정 규칙(대표 08-08~09):
//  - 목록 체크가 주력(58%) — 카드를 누르면 그 자리에서 아래로 펼쳐진다(화면 이동 없음)
//  - 칩 = "그 공정을 아직 안 한 환자 수" (할 일 기준, 포장 완료 건 제외) · 적용 공정만 센다
//  - 정렬 = 마감 임박순 고정 (지난 것 맨 위)
//  - 포장 완료 카드는 흐리게 4일 유지 후 목록에서만 제외 — 삭제 아님(의뢰서·명세서에 그대로)
//  - 금액·단가 절대 미표시
import React, { useMemo, useState } from 'react';
import CaseCheckView from './CaseCheckView';
import { deriveChecks, isHiddenAfterPacking, formatDday, CHECK_DEFS } from '../utils/caseChecks';

// 2026-08-18 디자인 통일 — 대표 확정: 파랑 #2a78d6 → 보라 #6366f1, 활성 칩 검정 → 보라 채움
const C = {
    ink: '#1e293b', sub: '#64748b', muted: '#94a3b8', line: '#e2e8f0',
    blue: '#6366f1', red: '#d03b3b', amber: '#d97706', green: '#0ca30c',
    plane: '#f8fafc',
};

const CHIP_LABEL = { scan: '스캔', mill: '밀링', implantMill: '임플란트 밀링', modelPrint: '모델', jigPrint: '지그', packing: '포장' };

const patientOf = (e) => e?._name || e?.patientName || '(환자명 없음)';
const clinicOf = (e) => e?._clinic || e?.partnerName || e?.clientNameInFile || e?.fromCompanyName || e?.dentistName || e?.fromUserName || '';
const teethTextOf = (e) => {
    const raw = Array.isArray(e?.selectedTeeth) ? e.selectedTeeth
        : Array.isArray(e?.teeth) ? e.teeth
            : Array.isArray(e?.teethDetails) ? e.teethDetails.map(d => d?.tooth || d?.toothNumber) : [];
    const list = raw.filter(Boolean).map(String);
    if (list.length === 0) return '';
    return '#' + list.slice(0, 8).join(',') + (list.length > 8 ? '…' : '');
};

const S = {
    wrap: { maxWidth: 560, margin: '0 auto' },
    sticky: {
        position: 'sticky', top: 0, zIndex: 5, background: C.plane,
        padding: '6px 0 8px', margin: '0 -2px',
    },
    search: {
        width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: 10,
        border: `1px solid ${C.line}`, fontSize: 15, fontFamily: 'inherit', background: '#fff',
    },
    chipRow: { display: 'flex', gap: 6, overflowX: 'auto', paddingTop: 8, WebkitOverflowScrolling: 'touch' },
    chip: {
        flexShrink: 0, padding: '7px 11px', borderRadius: 8, border: `1px solid ${C.line}`,
        background: '#fff', color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
        fontFamily: 'inherit', whiteSpace: 'nowrap',
    },
    chipOn: { background: '#6366f1', borderColor: '#6366f1', color: '#fff' },
    chipN: { fontWeight: 800, marginLeft: 4 },
    card: {
        background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12,
        padding: '13px 14px', marginTop: 8, cursor: 'pointer',
    },
    cardPacked: { opacity: 0.45 },
    row1: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 },
    pname: { fontSize: 16.5, fontWeight: 800, color: C.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    dday: { fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' },
    row2: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 },
    sub: { fontSize: 12, color: C.sub, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    prog: { fontSize: 12, fontWeight: 700, color: C.sub, whiteSpace: 'nowrap' },
    next: { color: C.blue },
    packedNote: { fontSize: 11.5, color: C.muted, marginTop: 5 },
    expand: { marginTop: 12, borderTop: `1px solid ${C.line}`, paddingTop: 12, cursor: 'default' },
    empty: { border: `1px solid ${C.line}`, borderRadius: 12, padding: 24, textAlign: 'center', fontSize: 14, color: '#9ca3af', marginTop: 10, background: '#ffffff' },
    hiddenLine: { fontSize: 12, color: C.muted, textAlign: 'center', padding: '14px 0 4px', lineHeight: 1.6 },
};

/**
 * props:
 *  - rows: [{ id, kind:'order'|'case', entity }]  (부모가 중복 제거까지 끝낸 목록)
 *  - onToggle(row, key, next): 저장. 실패 시 반드시 throw/reject (CaseCheckView 규약)
 */
export default function CaseCheckBoard({ rows, onToggle }) {
    const [q, setQ] = useState('');
    const [chip, setChip] = useState('all');
    const [openId, setOpenId] = useState(null);

    const derivedRows = useMemo(() => (rows || []).map(r => ({
        ...r,
        d: deriveChecks(r.entity),
        hidden: isHiddenAfterPacking(r.entity),
    })), [rows]);

    const hiddenCount = derivedRows.filter(r => r.hidden).length;
    const visible = derivedRows.filter(r => !r.hidden);

    // 검색 (환자명·치과명 부분 일치)
    const qn = q.trim().toLowerCase().replace(/\s+/g, '');
    const searched = qn
        ? visible.filter(r => (patientOf(r.entity) + clinicOf(r.entity)).toLowerCase().replace(/\s+/g, '').includes(qn))
        : visible;

    // 칩 카운트 — "그 공정을 아직 안 한(적용되는) 환자 수", 포장 완료 건 제외
    const chipCount = useMemo(() => {
        const m = { all: searched.length };
        for (const def of CHECK_DEFS) {
            m[def.key] = searched.filter(r => {
                if (def.key !== 'packing' && r.d.packed) return false;
                const it = r.d.items.find(i => i.key === def.key);
                return it && it.applicable && !it.done;
            }).length;
        }
        return m;
    }, [searched]);

    const filtered = chip === 'all' ? searched : searched.filter(r => {
        if (chip !== 'packing' && r.d.packed) return false;
        const it = r.d.items.find(i => i.key === chip);
        return it && it.applicable && !it.done;
    });

    // 마감 임박순 고정 (마감 없는 건 뒤, 포장 완료는 맨 아래)
    const sorted = [...filtered].sort((a, b) => {
        if (a.d.packed !== b.d.packed) return a.d.packed ? 1 : -1;
        const ta = a.d.deadline ? a.d.deadline.getTime() : Infinity;
        const tb = b.d.deadline ? b.d.deadline.getTime() : Infinity;
        return ta - tb;
    });

    return (
        <div style={S.wrap}>
            <div style={S.sticky}>
                <input
                    style={S.search}
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    placeholder="환자명·치과명 검색"
                />
                <div style={S.chipRow}>
                    {[['all', '전체'], ...CHECK_DEFS.map(d => [d.key, CHIP_LABEL[d.key] || d.label])].map(([k, label]) => (
                        <button key={k} type="button"
                            onClick={() => setChip(k)}
                            style={{ ...S.chip, ...(chip === k ? S.chipOn : {}) }}>
                            {label}
                            <span style={S.chipN}>{chipCount[k] ?? 0}</span>
                        </button>
                    ))}
                </div>
            </div>

            {sorted.length === 0 && (
                <div style={S.empty}>
                    {chip === 'all' ? '표시할 케이스가 없습니다.' : '이 공정을 기다리는 케이스가 없습니다.'}
                </div>
            )}

            {sorted.map(r => {
                const dday = formatDday(r.d.deadline, r.d.deadlineBasis);
                const ddayColor = dday
                    ? (dday.state === 'late' ? C.red : dday.state === 'today' ? C.amber : C.sub)
                    : C.muted;
                const open = openId === r.id;
                return (
                    <div key={r.id} style={{ ...S.card, ...(r.d.packed ? S.cardPacked : {}) }}
                        onClick={() => setOpenId(open ? null : r.id)}>
                        <div style={S.row1}>
                            <span style={S.pname}>
                                {r.entity?.isRemake === true ? '🔁 ' : ''}{patientOf(r.entity)}
                            </span>
                            <span style={{ ...S.dday, color: ddayColor }}>{dday ? dday.text : '마감 없음'}</span>
                        </div>
                        <div style={S.row2}>
                            <span style={S.sub}>{[clinicOf(r.entity), teethTextOf(r.entity)].filter(Boolean).join(' · ') || '치과 미확인'}</span>
                            <span style={S.prog}>
                                {r.d.progress}
                                {!r.d.packed && r.d.nextLabel && <span style={S.next}>{' · 다음 ' + r.d.nextLabel}</span>}
                            </span>
                        </div>
                        {r.d.packed && (
                            <div style={S.packedNote}>{'포장 완료 · 4일 뒤 이 목록에서 빠집니다 (의뢰서·명세서에는 그대로 남습니다)'}</div>
                        )}
                        {open && (
                            <div style={S.expand} onClick={e => e.stopPropagation()}>
                                <CaseCheckView entity={r.entity} onToggle={(key, next) => onToggle(r, key, next)} />
                            </div>
                        )}
                    </div>
                );
            })}

            {hiddenCount > 0 && (
                <div style={S.hiddenLine}>
                    {`포장 완료 후 4일이 지나 목록에서 빠진 케이스 ${hiddenCount}건 — 의뢰서·명세서·수집함에는 그대로 있습니다.`}
                </div>
            )}
        </div>
    );
}
